import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import MainLayout from "../Layouts/main";
import { useAuth } from "../context/AuthContext";
import Button from "../components/Button";
import { useBluelagoon, Bluelagoon } from "@bluelagoon";
import { getAllPlans, isSupportPlan } from "@utils";
import { trackGoogleAnalyticsPurchase, track } from "@crm/analytics";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { toast } from "sonner";

import { Card } from "@shadcn-in-nx/ui/card";

import { Sparkles, Wallet, Calendar, ArrowLeft } from "lucide-react";
import { BillingTabs } from "./components/BillingTabs";
import { PlansTab, PaymentMethodsTab, CreditsTab, UsageTab, SupportTab, InvoicesTab, ModuleCostsTab } from "./tabs";
import { SupportProvider } from "../Support/context/SupportContext";
import FAQ from "./components/FAQ";
import Security from "./components/Security.jsx";
import { formatPaymentError } from "./utils/paymentErrorHelper";
import { usePaymentStatus } from "./hooks/usePaymentStatus";
import { PaymentProcessingModal } from "./components/PaymentProcessingModal";
import { SubscriptionStatusBanner } from "./components/SubscriptionStatusBanner";
import { stripePromise } from "./utils/stripeConfig";

function BillingPageContent() {
    const stripe = useStripe();
    const { t, i18n } = useTranslation();
    const {
        user,
        trialStatus,
        isInTrial,
        companyAgentUICredit,
        handleGetCompanyAgentUICredit,
        getUser,
    } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("plans"); // Tab navigation state
    const [highlightPlatform, setHighlightPlatform] = useState(null); // Platform to highlight in credits tab
    const [creditsToPurchase, setCreditsToPurchase] = useState(50);
    const [perplexityCreditsToPurchase, setPerplexityCreditsToPurchase] =
        useState(50);
    const [referralCode, setReferralCode] = useState(null);
    const [paymentSuccessMessage, setPaymentSuccessMessage] = useState(null);
    const [isYearly, setIsYearly] = useState(true); // Default to yearly (better value)
    const [selectedPlan, setSelectedPlan] = useState(null); // For showing plan details
    const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false); // Control custom checkout modal
    const [paymentMethods, setPaymentMethods] = useState([]); // List of payment methods
    const [showAddCardModal, setShowAddCardModal] = useState(false); // Control add card modal
    const [planHoverCount, setPlanHoverCount] = useState(0);
    const [retryPaymentError, setRetryPaymentError] = useState(null);
    const [retryPaymentErrorDetails, setRetryPaymentErrorDetails] =
        useState(null);
    const [showFloatingSalesAssist, setShowFloatingSalesAssist] = useState(
        () => {
            // Check if user has permanently dismissed it - if so, never show
            const dismissed = localStorage.getItem(
                "floatingSalesAssistDismissed"
            );
            if (dismissed === "true") {
                return false; // User dismissed it, never show
            }
            // Start as false - will be shown by useEffect after conditions are met (20s or 2 hovers)
            return false;
        }
    );
    const [agentUITokenAmount, setAgentUITokenAmount] = useState(50); // Amount in dollars for AgentUI tokens ($5-$10,000)
    const AGENTUI_TOKEN_PRICE = 0.25; // Each token costs $0.25
    const formattedAgentUICredits = (
        agentUITokenAmount / AGENTUI_TOKEN_PRICE
    ).toLocaleString();

    const [showCreditExamples, setShowCreditExamples] = useState(false);
    const [showCreditDefinition, setShowCreditDefinition] = useState(false);
    const [returnUrl] = useState(() => {
        const url = localStorage.getItem("billing_return_url");
        if (url) {
            return url;
        }
        return null;
    });
    const navigate = useNavigate();

    // Handle 3DS payment returns
    const { isProcessing: isPaymentProcessing, processingMessage } =
        usePaymentStatus({
            onSuccess: ({ platform }) => {
                // Refresh credits and user data after successful payment
                handleGetCompanyAgentUICredit();
                getUser();
            },
        });

    const didGetCompanyAgentUICreditRef = useRef(false);
    useEffect(() => {
        // eslint-disable-next-line no-undef
        try {
            if (
                typeof window !== "undefined" &&
                typeof rewardful !== "undefined"
            ) {
                // eslint-disable-next-line no-undef
                rewardful("ready", function () {
                    // eslint-disable-next-line no-undef
                    if (typeof Rewardful !== "undefined") {
                        // eslint-disable-next-line no-undef
                        setReferralCode(Rewardful.referral);
                    }
                });
            }
        } catch (error) {
            console.error("Error initializing rewardful:", error);
        }
    }, []);

    useEffect(() => {
        if (didGetCompanyAgentUICreditRef.current) return;
        setTimeout(() => {
            handleGetCompanyAgentUICredit();
        }, 2000);
        didGetCompanyAgentUICreditRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle URL hash for deep linking to credits tab with platform highlight
    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash === "credit-purchase") {
            setActiveTab("credits");
            setHighlightPlatform("openai");
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (hash === "perplexity-credit-purchase") {
            setActiveTab("credits");
            setHighlightPlatform("perplexity");
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: "smooth" });
        } else if (hash === "support") {
            setActiveTab("support");
            // Scroll to top of page
            window.scrollTo({ top: 0, behavior: "smooth" });
        }
    }, []);

    // Handle successful payment tracking
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get("session_id");
        const creditsSuccess = urlParams.get("credits_success");
        const perplexityCreditsSuccess = urlParams.get(
            "perplexity_credits_success"
        );
        const agentUITokensSuccess = urlParams.get("agentui_tokens_success");

        if (sessionId) {
            handleSuccessfulPayment(sessionId);
        }

        // If returning from a successful credit purchase via Stripe checkout,
        // refresh the user data and credits to show the updated balance
        if (
            creditsSuccess ||
            perplexityCreditsSuccess ||
            agentUITokensSuccess
        ) {
            // Retry fetching credits with exponential backoff to ensure webhook has processed
            const refreshCreditsWithRetry = async (
                retries = 3,
                delay = 1000
            ) => {
                for (let i = 0; i < retries; i++) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, delay * (i + 1))
                    );
                    await Promise.all([
                        getUser(),
                        handleGetCompanyAgentUICredit(),
                    ]);
                }
                // Clean up URL parameters after final refresh
                const url = new URL(window.location);
                url.searchParams.delete("credits_success");
                url.searchParams.delete("perplexity_credits_success");
                url.searchParams.delete("agentui_tokens_success");
                url.searchParams.delete("platform");
                window.history.replaceState({}, "", url);
            };
            refreshCreditsWithRetry();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle scrolling to hash on page load
    useEffect(() => {
        const hash = window.location.hash;
        if (hash) {
            // Wait for content to render
            setTimeout(() => {
                const element = document.querySelector(hash);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, 100);
        }
    }, []);

    // Track page view
    useEffect(() => {
        track("billing_page_viewed");

        // Track page exit
        const handleBeforeUnload = () => {
            track("billing_page_exited");
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    // Floating sales-assist: Show after 20s on page (only if not dismissed)
    useEffect(() => {
        if (showFloatingSalesAssist) return; // Don't set timer if already shown

        const timer = setTimeout(() => {
            // Check again before showing (in case user dismissed during wait)
            const dismissed = localStorage.getItem(
                "floatingSalesAssistDismissed"
            );
            if (dismissed !== "true") {
                setShowFloatingSalesAssist(true);
            }
        }, 20000); // 20 seconds

        return () => clearTimeout(timer);
    }, [showFloatingSalesAssist]);

    // Floating sales-assist: Show after 2+ plan hovers (only if not dismissed)
    useEffect(() => {
        if (planHoverCount >= 2) {
            // Check if not dismissed before showing
            const dismissed = localStorage.getItem(
                "floatingSalesAssistDismissed"
            );
            if (dismissed !== "true") {
                setShowFloatingSalesAssist(true);
            }
        }
    }, [planHoverCount]);

    const refreshPaymentMethods = async () => {
        if (user?.id) {
            try {
                const response = await getPaymentMethods({
                    userId: user.id,
                });
                if (response?.data?.paymentMethods) {
                    setPaymentMethods(response.data.paymentMethods);
                }
            } catch (error) {
                console.error("Error fetching payment methods:", error);
            }
        }
    };

    // Fetch payment methods when component mounts
    useEffect(() => {
        refreshPaymentMethods();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const handleSuccessfulPayment = async (sessionId) => {
        try {
            const response = await getSessionDetails({ sessionId });

            if (response?.data && response.status === 200) {
                const sessionData = response.data;

                // Track purchase with Google Analytics
                if (
                    sessionData.paymentStatus === "paid" ||
                    sessionData.paymentStatus === "complete"
                ) {
                    // Get plan name from metadata or line items
                    const planName =
                        sessionData.metadata?.planName ||
                        sessionData.lineItems?.[0]?.description ||
                        "Subscription";

                    trackGoogleAnalyticsPurchase(
                        sessionData.sessionId,
                        sessionData.amount / 100, // Convert cents to dollars
                        sessionData.currency?.toUpperCase() || "USD",
                        sessionData.metadata?.priceId ||
                            sessionData.lineItems?.[0]?.price?.id,
                        planName
                    );

                    track("purchase", {
                        amount: sessionData.amount / 100,
                        planName,
                    });

                    setPaymentSuccessMessage(
                        t("billing.paymentSuccess", {
                            amount: (sessionData.amount / 100).toFixed(2),
                            planName,
                        })
                    );
                }
            }
        } catch (error) {
            console.error("Error tracking payment:", error);
        }
    };
    const {
        call: createSubscriptionCheckout,
        error: checkoutError,
        isLoading: checkoutLoading,
    } = useBluelagoon({
        method: "createSubscriptionCheckout",
    });

    const {
        call: insertNewStripeSession,

        isLoading: cancelLoading,
    } = useBluelagoon({
        method: "insertNewStripeSession",
    });

    const {
        call: createCreditCheckout,
        error: creditCheckoutError,
        isLoading: creditCheckoutLoading,
    } = useBluelagoon({
        method: "createCreditCheckout",
    });

    const { call: getSessionDetails } = useBluelagoon({
        method: "getSessionDetails",
    });

    const {
        call: updateSubscription,
        error: updateError,
        isLoading: updateLoading,
    } = useBluelagoon({
        method: "updateSubscription",
    });

    const {
        call: resumeSubscription,
        error: resumeError,
        isLoading: resumeLoading,
    } = useBluelagoon({
        method: "resumeSubscription",
    });

    const { call: getPaymentMethods, isLoading: paymentMethodsLoading } =
        useBluelagoon({
            method: "getPaymentMethods",
        });

    const { call: addPaymentMethod } = useBluelagoon({
        method: "addPaymentMethod",
    });

    const { call: removePaymentMethod, isLoading: removingPaymentMethod } =
        useBluelagoon({
            method: "removePaymentMethod",
        });

    const {
        call: setDefaultPaymentMethod,
        isLoading: settingDefaultPaymentMethod,
    } = useBluelagoon({
        method: "setDefaultPaymentMethod",
    });

    const { call: cancelSubscription, isLoading: cancelingSubscription } =
        useBluelagoon({
            method: "cancelSubscription",
        });

    const { call: pauseSubscription, isLoading: pausingSubscription } =
        useBluelagoon({
            method: "pauseSubscription",
        });

    // Wrapper for updateSubscription that handles 3DS inline
    const updateSubscriptionWith3DS = async (params) => {
        const response = await updateSubscription(params);

        // Extract response data (axios wraps response in data)
        const responseData = response?.data?.data || response?.data;

        // Check if 3DS authentication is required
        if (responseData?.requiresAction && responseData?.clientSecret) {
            if (!stripe) {
                return {
                    status: 500,
                    error: "Payment system not ready. Please try again.",
                };
            }

            const { error: confirmError, paymentIntent } =
                await stripe.confirmCardPayment(responseData.clientSecret, {
                    payment_method: responseData.paymentMethodId,
                });

            if (confirmError) {
                return {
                    status: 402,
                    error: confirmError.message || "Authentication failed",
                    code: confirmError.code,
                };
            }

            if (paymentIntent.status === "succeeded") {
                return {
                    status: 200,
                    data: {
                        message: "Subscription updated successfully",
                        subscription: { status: "active" },
                    },
                };
            } else {
                return {
                    status: 402,
                    error: `Payment not completed. Status: ${paymentIntent.status}`,
                };
            }
        }

        return response;
    };

    const plans = getAllPlans();

    // Get all subscriptions
    const allSubscriptions = user?.company?.stripeCustomer?.subscriptions || [];

    // Filter out support subscriptions — they are displayed separately in the Support tab
    const planSubscriptions = allSubscriptions.filter(
        (sub) => !isSupportPlan(sub.priceId)
    );

    // Priority order: ACTIVE > TRIALING > PAST_DUE > INCOMPLETE > others
    const statusPriority = {
        ACTIVE: 1,
        TRIALING: 2,
        PAST_DUE: 3,
        INCOMPLETE: 4,
        INCOMPLETE_EXPIRED: 5,
        UNPAID: 6,
        CANCELED: 7,
    };

    // Find the primary subscription (for current plan display)
    const primarySubscription =
        planSubscriptions.length > 0
            ? planSubscriptions.reduce((prev, current) => {
                  const prevPriority = statusPriority[prev.status] || 999;
                  const currentPriority = statusPriority[current.status] || 999;
                  return currentPriority < prevPriority ? current : prev;
              })
            : null;

    const currentPlanId = primarySubscription?.priceId;
    // Get current plan details
    const currentPlan = plans.find(
        (plan) =>
            (plan.priceIdMonthly === currentPlanId && plan.priceIdMonthly) ||
            (plan.priceIdYearly === currentPlanId && plan.priceIdYearly) ||
            (plan.devPriceIdMonthly === currentPlanId &&
                plan.devPriceIdMonthly) ||
            (plan.devPriceIdYearly === currentPlanId && plan.devPriceIdYearly)
    )?.name;

    const currentPlanDetails = plans.find(
        (plan) =>
            (plan.priceIdMonthly === currentPlanId && plan.priceIdMonthly) ||
            (plan.priceIdYearly === currentPlanId && plan.priceIdYearly) ||
            (plan.devPriceIdMonthly === currentPlanId &&
                plan.devPriceIdMonthly) ||
            (plan.devPriceIdYearly === currentPlanId && plan.devPriceIdYearly)
    );
    const currentSubscription = primarySubscription;

    // Helper function to get status badge color
    const getStatusColor = (status) => {
        switch (status) {
            case "ACTIVE":
                return "bg-green-100 text-green-800 border-green-300";
            case "TRIALING":
                return "bg-blue-100 text-blue-800 border-blue-300";
            case "PAST_DUE":
                return "bg-yellow-100 text-yellow-800 border-yellow-300";
            case "INCOMPLETE":
                return "bg-orange-100 text-orange-800 border-orange-300";
            case "INCOMPLETE_EXPIRED":
                return "bg-red-100 text-red-800 border-red-300";
            case "CANCELED":
                return "bg-gray-100 text-gray-800 border-gray-300";
            case "UNPAID":
                return "bg-red-100 text-red-800 border-red-300";
            default:
                return "bg-gray-100 text-gray-800 border-gray-300";
        }
    };

    // Helper function to format status name
    const formatStatus = (status) => {
        const statusKey = `billing.status.${status.toLowerCase()}`;
        const translated = t(statusKey);
        // If translation exists, use it; otherwise fall back to formatted status
        if (translated !== statusKey) {
            return translated;
        }
        return status
            .split("_")
            .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
            .join(" ");
    };

    const handleSubscriptionCheckout = async (productId, yearly = isYearly) => {
        try {
            setIsLoading(true);

            // Track checkout start event
            const plan = plans.find((p) => getProductId(p) === productId);
            track("checkout_started", {
                plan: plan?.name || "Unknown",
            });

            const response = await createSubscriptionCheckout({
                productId,
                isYearly: yearly,
                successUrl: `${window.location.origin}/billing?success=true`,
                cancelUrl: `${window.location.origin}/billing?canceled=true`,
                referral: referralCode,
            });

            if (response?.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreditPurchase = async (amount) => {
        setIsLoading(true);
        try {
            const response = await createCreditCheckout({
                amount: typeof amount === "number" ? amount : creditsToPurchase, // Use passed amount or state
                platform: "openai",
                successUrl: `${window.location.origin}/billing?credits_success=true`,
                cancelUrl: `${window.location.origin}/billing?credits_canceled=true`,
            });

            if (response?.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error("Error purchasing credits:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePerplexityCreditPurchase = async (amount) => {
        setIsLoading(true);
        try {
            const response = await createCreditCheckout({
                amount:
                    typeof amount === "number"
                        ? amount
                        : perplexityCreditsToPurchase,
                platform: "perplexity",
                successUrl: `${window.location.origin}/billing?perplexity_credits_success=true`,
                cancelUrl: `${window.location.origin}/billing?perplexity_credits_canceled=true`,
            });

            if (response?.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error("Error purchasing Perplexity credits:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAgentUITokenPurchase = async (amount) => {
        setIsLoading(true);
        try {
            const response = await createCreditCheckout({
                amount:
                    typeof amount === "number" ? amount : agentUITokenAmount, // Amount in dollars
                platform: "agentui",
                successUrl: `${window.location.origin}/billing?agentui_tokens_success=true`,
                cancelUrl: `${window.location.origin}/billing?agentui_tokens_canceled=true`,
            });

            if (response?.data?.url) {
                window.location.href = response.data.url;
            }
        } catch (error) {
            console.error("Error purchasing AgentUI tokens:", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to get the correct productId based on environment
    const getProductId = (plan) => {
        return (Bluelagoon.isDevelopment || Bluelagoon.isStaging) && plan.devProductId
            ? plan.devProductId
            : plan.productId;
    };

    const handleBillingPortal = async (productId) => {
        try {
            if (currentPlan == null) {
                handleSubscriptionCheckout(productId);
                return;
            }

            setIsLoading(true);
            // Get Facebook Pixel cookies and include them in the request
            const facebookCookies = Bluelagoon.getFacebookPixelCookies();
            const response = await insertNewStripeSession({
                ...facebookCookies,
            });
            // Refresh the page to show updated subscription status
            if (response.data.sessionUrl) {
                window.location.href = response.data.sessionUrl;
            }
        } catch (error) {
            console.error("Error canceling subscription:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePlanSelection = (plan) => {
        // Track plan card click
        track("plan_card_clicked", {
            plan: plan.name,
        });

        // Check for calendar booking plans first (before "Contact us" check)
        if (plan.name === "Build with You" || plan.name === "Enterprise Plus") {
            // Open calendar in new tab
            const calendarUrl = "https://calendar.app.google/r1NEgK7oWPMoUxur9";
            window.open(calendarUrl, "_blank", "noopener,noreferrer");
            return;
        }

        if (plan.price === "Contact us") {
            window.location.href = "https://www.agentui.ai/contact/";
            return;
        }
        setSelectedPlan(plan);
    };

    // Handle removing a payment method
    const handleRemovePaymentMethod = async (paymentMethodId) => {
        if (
            !window.confirm(
                t("billing.confirmRemoveCard") ||
                    "Are you sure you want to remove this card?"
            )
        ) {
            return;
        }

        try {
            const response = await removePaymentMethod({
                userId: user.id,
                paymentMethodId,
            });

            if (response?.status === 200) {
                await refreshPaymentMethods();
                track("payment_method_removed");
            }
        } catch (error) {
            console.error("Error removing payment method:", error);
            toast.error("Failed to remove payment method");
        }
    };

    // Handle setting default payment method
    const handleSetDefaultPaymentMethod = async (paymentMethodId) => {
        try {
            const response = await setDefaultPaymentMethod({
                userId: user.id,
                paymentMethodId,
            });

            if (response?.status === 200) {
                await refreshPaymentMethods();
                track("payment_method_set_default");
            }
        } catch (error) {
            console.error("Error setting default payment method:", error);
            toast.error("Failed to set default payment method");
        }
    };

    // Calculate savings
    const calculateSavings = (plan) => {
        if (plan.price === "Contact us") return 0;
        const monthlyTotal = parseFloat(plan.price) * 12;
        const yearlyTotal = parseFloat(plan.priceYearly) * 12;
        return monthlyTotal - yearlyTotal;
    };

    // Handle subscription cancellation
    const handleCancelSubscription = async (reason) => {
        try {
            const response = await cancelSubscription({ reason });
            if (response?.status === 200) {
                // Refresh the page to show updated subscription status
                window.location.reload();
            }
        } catch (error) {
            console.error("Error canceling subscription:", error);
            toast.error("Failed to cancel subscription. Please try again.");
        }
    };

    // Handle subscription pause
    const handlePauseSubscription = async () => {
        try {
            const response = await pauseSubscription();
            if (response?.status === 200) {
                // Refresh the page to show updated subscription status
                window.location.reload();
            }
        } catch (error) {
            console.error("Error pausing subscription:", error);
            toast.error("Failed to pause subscription. Please try again.");
        }
    };

    // Handle subscription resume
    const handleResumeSubscription = async () => {
        try {
            const response = await resumeSubscription();
            if (response?.status === 200) {
                toast.success("Subscription resumed successfully!");
                // Refresh the page to show updated subscription status
                window.location.reload();
            }
        } catch (error) {
            console.error("Error resuming subscription:", error);
            toast.error("Failed to resume subscription. Please try again.");
        }
    };

    // Handle contact support
    const handleContactSupport = () => {
        // Open calendar booking
        window.open(
            "https://calendar.app.google/r1NEgK7oWPMoUxur9",
            "_blank",
            "noopener,noreferrer"
        );
    };

    // Handle retrying payment for incomplete subscription
    const handleRetryPayment = async () => {
        try {
            setIsLoading(true);
            setRetryPaymentError(null);
            setRetryPaymentErrorDetails(null);

            // Get the current subscription details
            if (!currentSubscription || !currentPlanDetails) {
                toast.error("No subscription found to retry");
                return;
            }

            // Determine if the current subscription is yearly
            const isCurrentYearly =
                currentPlanId === currentPlanDetails.priceIdYearly ||
                currentPlanId === currentPlanDetails.devPriceIdYearly;

            // Retry the subscription with the same plan (uses wrapper that handles 3DS inline)
            const response = await updateSubscriptionWith3DS({
                userId: user.id,
                productId: getProductId(currentPlanDetails),
                isYearly: isCurrentYearly,
            });

            if (response?.status === 200) {
                toast.success(
                    "Payment successful! Your subscription is now active."
                );
                // Refresh the page to show updated subscription status
                window.location.reload();
            } else {
                // Show detailed error message
                const errorInfo = formatPaymentError(
                    response?.error || "Failed to retry payment",
                    response,
                    t
                );
                setRetryPaymentError(response?.error || "Failed to retry payment");
                setRetryPaymentErrorDetails(errorInfo);
            }
        } catch (error) {
            console.error("Error retrying payment:", error);

            // Check if this is an API error response
            const isApiError =
                error && typeof error === "object" && "status" in error;

            // Show detailed error message
            const errorInfo = formatPaymentError(
                isApiError
                    ? error.error || "Failed to retry payment"
                    : error?.message || "Failed to retry payment",
                isApiError ? error : { status: 500 },
                t
            );
            setRetryPaymentError(
                isApiError
                    ? error.error || "Failed to retry payment"
                    : error?.message || "Failed to retry payment"
            );
            setRetryPaymentErrorDetails(errorInfo);
        } finally {
            setIsLoading(false);
        }
    };

    // Helper function to determine if a plan is an upgrade or downgrade
    const getPlanComparison = (plan) => {
        const isUpdateable =
            currentSubscription &&
            !isInTrial &&
            ["active", "past_due"].includes(
                currentSubscription.status?.toLowerCase()
            );

        if (!currentPlanDetails || !isUpdateable) {
            return null; // No comparison if no current plan or in trial
        }

        // Get current plan price (use yearly if current subscription is yearly)
        const isCurrentYearly =
            currentPlanId === currentPlanDetails.priceIdYearly ||
            currentPlanId === currentPlanDetails.devPriceIdYearly;
        const currentPrice = isCurrentYearly
            ? parseFloat(currentPlanDetails.priceYearly || 0)
            : parseFloat(currentPlanDetails.price || 0);

        // Get target plan price
        const targetPrice = isYearly
            ? parseFloat(plan.priceYearly || 0)
            : parseFloat(plan.price || 0);

        if (plan.price === "Contact us") {
            return null; // Can't compare with enterprise
        }

        if (targetPrice > currentPrice) {
            return "upgrade";
        } else if (targetPrice < currentPrice) {
            return "downgrade";
        } else {
            return "same"; // Same price (might be switching billing cycle)
        }
    };

    const paymentUpdateErrorCodes = [
        "payment_failed",
        "payment_required",
        "requires_payment_method",
        "requires_action",
        "requires_confirmation",
    ];
    const isPaymentUpdateError =
        !!updateError &&
        (Number(updateError.status) === 402 ||
            paymentUpdateErrorCodes.includes(
                String(updateError.code || "").toLowerCase()
            ));
    const pendingInvoiceUrl = updateError?.data?.hostedInvoiceUrl;
    const subscriptionAttentionStatuses = [
        "PAST_DUE",
        "INCOMPLETE",
        "INCOMPLETE_EXPIRED",
        "UNPAID",
    ];
    const subscriptionRequiresAttention =
        !isInTrial &&
        subscriptionAttentionStatuses.includes(
            currentSubscription?.status ?? ""
        );

    return (
        <MainLayout>
            <style>{`
                @keyframes glow {
                    from {
                        box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.5), 0 0 20px rgba(59, 130, 246, 0.3);
                    }
                    to {
                        box-shadow: 0 10px 35px -5px rgba(59, 130, 246, 0.7), 0 0 30px rgba(59, 130, 246, 0.5);
                    }
                }

                /* Custom scrollbar for modal content */
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }

                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 10px;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }

                /* Smooth transitions for inputs */
                input:focus, .StripeElement--focus {
                    transition: all 0.15s ease;
                }
                /* iOS Safari fixes */
                @supports (-webkit-touch-callout: none) {
                    .safe-area-inset {
                        padding-top: env(safe-area-inset-top);
                        padding-bottom: env(safe-area-inset-bottom);
                        padding-left: env(safe-area-inset-left);
                        padding-right: env(safe-area-inset-right);
                    }
                }
                /* Prevent iOS Safari bounce scroll in modal */
                .overscroll-contain {
                    overscroll-behavior: contain;
                    -webkit-overflow-scrolling: touch;
                }
                /* Android-specific modal fixes */
                @media (max-width: 768px) {
                    .billing-modal-overlay {
                        position: fixed !important;
                        z-index: 9999 !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        height: 100dvh !important; /* Dynamic viewport height for Android */
                    }
                    .billing-modal-content {
                        max-height: 85vh !important;
                        max-height: 85dvh !important; /* Dynamic viewport height for Android */
                    }
                }
            `}</style>
            <div className="billing-page-theme min-h-screen p-4 md:p-8">
                <div className="max-w-6xl mx-auto space-y-6">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                                {t("billing.pageHeading")}
                            </h1>
                            <p className="text-muted-foreground">
                                {t("billing.pageSubheading")}
                            </p>
                            <p className="text-xs text-muted-foreground/80 mt-1">
                                {t("billing.pricesInUsd")}
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            {returnUrl && (
                                <Button
                                    onClick={() => {
                                        localStorage.removeItem(
                                            "billing_return_url"
                                        );
                                        navigate(returnUrl);
                                    }}
                                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium flex items-center gap-2"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    {t("billing.backToApp")}
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Consolidated Status Cards */}
                    <div className="grid md:grid-cols-2 gap-4">
                        <Card className="p-6 border-border/50 shadow-[var(--shadow-soft)] border-2">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-secondary flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">
                                            {t("billing.statusCards.planLabel")}
                                        </p>
                                        <p className="text-2xl font-bold text-foreground">
                                            {currentPlanDetails?.name ||
                                                (isInTrial
                                                    ? t("billing.freeTrial")
                                                    : t(
                                                          "billing.noActivePlan"
                                                      ))}
                                        </p>
                                    </div>
                                </div>
                                {currentSubscription ? (
                                    <Badge
                                        variant="secondary"
                                        className={`text-xs font-semibold ${getStatusColor(
                                            currentSubscription.status
                                        )}`}
                                    >
                                        {formatStatus(
                                            currentSubscription.status
                                        )}
                                    </Badge>
                                ) : null}
                            </div>
                            {currentPlanDetails && currentSubscription && !currentSubscription.cancelAtPeriodEnd && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                                    <span className="text-xs text-emerald-600 font-medium">✔ {t("billing.statusCards.planFeature.apps") || "Unlimited apps"}</span>
                                    <span className="text-xs text-emerald-600 font-medium">✔ {t("billing.statusCards.planFeature.aiCapacity") || "AI capacity included"}</span>
                                    <span className="text-xs text-emerald-600 font-medium">✔ {t("billing.statusCards.planFeature.users", { count: currentPlanDetails.numberOfUsers || "5" }) || `${currentPlanDetails.numberOfUsers || "5"} internal users`}</span>
                                </div>
                            )}
                            <p className="text-sm text-muted-foreground mt-2">
                                {currentSubscription?.currentPeriodEnd &&
                                currentSubscription.cancelAtPeriodEnd
                                    ? t("billing.statusCards.expiresOn", {
                                          date: new Date(
                                              currentSubscription.currentPeriodEnd
                                          ).toLocaleDateString(),
                                      })
                                    : isInTrial && trialStatus
                                    ? t("billing.trialUrgencyWarning")
                                    : currentSubscription
                                    ? ""
                                    : t("billing.planSubtitle")}
                            </p>
                        </Card>
                        <Card className="p-6 border-border/50 shadow-[var(--shadow-soft)] border-2">
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
                                        <Wallet className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-muted-foreground">
                                            {t("billing.statusCards.capacityLabel") || "AI Capacity"}
                                        </p>
                                        <p className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-primary">
                                            {(() => {
                                                const bal = Number(companyAgentUICredit) || 0;
                                                if (bal >= 100) return t("billing.capacity.high") || "High";
                                                if (bal >= 20) return t("billing.capacity.normal") || "Normal";
                                                if (bal >= 2) return t("billing.capacity.low") || "Low";
                                                return t("billing.capacity.depleted") || "Depleted";
                                            })()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {(() => {
                                const bal = Number(companyAgentUICredit) || 0;
                                const monthly = currentPlanDetails?.credits ? parseInt(currentPlanDetails.credits) : 200;
                                const pct = Math.min(100, Math.max(0, Math.round(((monthly - bal) / monthly) * 100)));
                                const screens = Math.floor(bal / 25);
                                const estimate = screens >= 1000 ? `${Math.floor(screens / 1000).toLocaleString()},000+` : screens.toLocaleString();
                                const barColor = bal >= 100 ? "bg-emerald-500" : bal >= 20 ? "bg-blue-500" : "bg-yellow-500";
                                return (
                                    <>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-3 mb-2">
                                            <div className={`${barColor} h-2 rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {pct}% {t("billing.capacity.used") || "used"} · {Math.round(bal).toLocaleString()} {t("billing.creditsRemaining") || "credits remaining"}
                                        </p>
                                        {screens > 0 && (
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                ≈ {t("billing.capacity.enoughToGenerate", { screens: estimate }) || `enough to generate ${estimate} app screens`}
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </Card>
                    </div>

                    {/* Subscription Status Banner */}
                    {currentSubscription && (
                        <SubscriptionStatusBanner
                            subscription={currentSubscription}
                            onRetryPayment={handleRetryPayment}
                            onResumeSubscription={handleResumeSubscription}
                            onUpdatePaymentMethod={() =>
                                setActiveTab("payment")
                            }
                            onPurchaseCredits={() => setActiveTab("credits")}
                            onContactSupport={handleContactSupport}
                            isLoading={isLoading || updateLoading || resumeLoading}
                            retryError={retryPaymentError}
                            retryErrorDetails={retryPaymentErrorDetails}
                            onDismissError={() => {
                                setRetryPaymentError(null);
                                setRetryPaymentErrorDetails(null);
                            }}
                            t={t}
                        />
                    )}

                    {/* Old banner - replaced by SubscriptionStatusBanner above */}
                    {false && subscriptionRequiresAttention && (
                        <Card className="p-6 bg-yellow-50 border-2 border-yellow-200 shadow-lg">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0">
                                    <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                                        <svg
                                            className="w-6 h-6 text-white"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-base font-bold text-gray-900 mb-2">
                                        {t("billing.statusCards.noticeTitle")}
                                    </h3>
                                    <p className="text-sm text-gray-700 mb-4">
                                        {t("billing.statusCards.noticeMessage")}
                                    </p>

                                    {/* Retry Payment Error Display */}
                                    {retryPaymentError &&
                                        retryPaymentErrorDetails && (
                                            <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg mb-4">
                                                <div className="flex items-start">
                                                    <div className="flex-shrink-0">
                                                        <svg
                                                            className="w-5 h-5 text-red-500 mt-0.5"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3 flex-1">
                                                        <h4 className="text-sm font-bold text-red-800">
                                                            {
                                                                retryPaymentErrorDetails.title
                                                            }
                                                        </h4>
                                                        <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
                                                            {
                                                                retryPaymentErrorDetails.message
                                                            }
                                                        </p>
                                                        {retryPaymentErrorDetails.action && (
                                                            <p className="text-xs text-red-600 mt-2 font-medium">
                                                                💡{" "}
                                                                {
                                                                    retryPaymentErrorDetails.action
                                                                }
                                                            </p>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setRetryPaymentError(
                                                                null
                                                            );
                                                            setRetryPaymentErrorDetails(
                                                                null
                                                            );
                                                        }}
                                                        className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                                                    >
                                                        <svg
                                                            className="w-4 h-4"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M6 18L18 6M6 6l12 12"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                    <div className="flex flex-wrap gap-3">
                                        {currentSubscription?.status ===
                                            "INCOMPLETE" && (
                                            <Button
                                                onClick={handleRetryPayment}
                                                disabled={
                                                    isLoading || updateLoading
                                                }
                                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-2"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                                    />
                                                </svg>
                                                {isLoading || updateLoading
                                                    ? t("billing.processing") ||
                                                      "Processing..."
                                                    : t(
                                                          "billing.statusCards.retryPayment"
                                                      ) || "Retry Payment"}
                                            </Button>
                                        )}
                                        <Button
                                            onClick={() =>
                                                setActiveTab("payment")
                                            }
                                            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                                />
                                            </svg>
                                            {t(
                                                "billing.statusCards.updatePaymentMethod"
                                            ) || "Update Payment Method"}
                                        </Button>
                                        <Button
                                            onClick={() =>
                                                setActiveTab("credits")
                                            }
                                            className="bg-white hover:bg-gray-50 text-gray-700 border-2 border-gray-300 px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                />
                                            </svg>
                                            {t(
                                                "billing.statusCards.purchaseCredits"
                                            ) || "Purchase Credits"}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    )}
                    {/* Success/Error Messages */}
                    {checkoutError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6">
                            {t("billing.checkoutError")}
                        </div>
                    )}
                    {updateError && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg mb-6 space-y-2">
                            <p className="font-semibold">
                                {isPaymentUpdateError
                                    ? t("billing.paymentMethodDeclined")
                                    : t("billing.manageSubscriptionError")}
                            </p>
                            {isPaymentUpdateError ? (
                                <>
                                    {updateError.error && (
                                        <p className="text-sm text-red-600">
                                            {updateError.error}
                                        </p>
                                    )}
                                    <p className="text-sm text-red-600">
                                        {t("billing.paymentMethodDeclinedHelp")}
                                    </p>
                                    <div className="flex flex-wrap gap-3 pt-2">
                                        {pendingInvoiceUrl && (
                                            <a
                                                href={pendingInvoiceUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-white text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                                            >
                                                {t("billing.reviewInvoice")}
                                            </a>
                                        )}
                                        <Button
                                            onClick={() =>
                                                handleBillingPortal()
                                            }
                                            disabled={
                                                isLoading || cancelLoading
                                            }
                                            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                                        >
                                            {t("billing.retryPlanChange")}
                                        </Button>
                                    </div>
                                </>
                            ) : (
                                updateError.error && (
                                    <p className="text-sm text-red-600">
                                        {updateError.error}
                                    </p>
                                )
                            )}
                        </div>
                    )}
                    {paymentSuccessMessage && (
                        <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-lg mb-6">
                            {paymentSuccessMessage}
                        </div>
                    )}

                    {/* Tab Navigation */}
                    <BillingTabs
                        activeTab={activeTab}
                        onTabChange={setActiveTab}
                        hasActiveSubscription={primarySubscription && ['active', 'trialing'].includes(primarySubscription.status?.toLowerCase())}
                        t={t}
                    />

                    {/* AgentUI Credits Section */}

                    {/* Consulting Hours Section
                <div className="bg-gray-100  rounded-lg p-6 mb-8">
                    <h2 className="text-2xl font-semibold mb-4">
                        Purchase Consulting Hours
                    </h2>
                    <p className="text-gray-600  mb-4">
                        Get expert help with your automation needs. Our
                        consultants will help you build, optimize, and maintain
                        your workflows.
                    </p>
                    <div className="flex items-center gap-4 mb-4">
                        <input
                            type="number"
                            min="1"
                            value={consultingHours}
                            onChange={(e) =>
                                setConsultingHours(parseInt(e.target.value))
                            }
                            className="w-24 px-3 py-2 border rounded-md"
                        />
                        <span>hours</span>
                        <button
                            onClick={handleConsultingPurchase}
                            disabled={isLoading}
                            className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
                        >
                            {isLoading ? "Processing..." : "Purchase Hours"}
                        </button>
                    </div>
                    <p className="text-sm text-gray-500">
                        Rate: $200/hour - Minimum 1 hour purchase
                    </p>
                </div> */}

                    {/* Payment Methods Tab */}
                    {activeTab === "payment" && (
                        <PaymentMethodsTab
                            paymentMethods={paymentMethods}
                            paymentMethodsLoading={paymentMethodsLoading}
                            showAddCardModal={showAddCardModal}
                            setShowAddCardModal={setShowAddCardModal}
                            onRemovePaymentMethod={handleRemovePaymentMethod}
                            onSetDefaultPaymentMethod={
                                handleSetDefaultPaymentMethod
                            }
                            removingPaymentMethod={removingPaymentMethod}
                            settingDefaultPaymentMethod={
                                settingDefaultPaymentMethod
                            }
                            user={user}
                            addPaymentMethod={addPaymentMethod}
                            refreshPaymentMethods={refreshPaymentMethods}
                            t={t}
                        />
                    )}

                    {/* Subscription Plans Section */}
                    {/* Plans Tab */}
                    {activeTab === "plans" && (
                        <>
                            <PlansTab
                                plans={plans}
                                currentPlanDetails={currentPlanDetails}
                                currentPlanId={currentPlanId}
                                currentSubscription={currentSubscription}
                                isInTrial={isInTrial}
                                isYearly={isYearly}
                                setIsYearly={setIsYearly}
                                selectedPlan={selectedPlan}
                                setSelectedPlan={setSelectedPlan}
                                showEmbeddedCheckout={showEmbeddedCheckout}
                                setShowEmbeddedCheckout={
                                    setShowEmbeddedCheckout
                                }
                                isLoading={isLoading}
                                checkoutLoading={checkoutLoading}
                                updateLoading={updateLoading}
                                resumeLoading={resumeLoading}
                                cancelLoading={cancelLoading}
                                user={user}
                                paymentMethods={paymentMethods}
                                addPaymentMethod={addPaymentMethod}
                                updateSubscription={updateSubscriptionWith3DS}
                                resumeSubscription={resumeSubscription}
                                createSubscriptionCheckout={
                                    createSubscriptionCheckout
                                }
                                handleBillingPortal={handleBillingPortal}
                                calculateSavings={calculateSavings}
                                getPlanComparison={getPlanComparison}
                                getProductId={getProductId}
                                planHoverCount={planHoverCount}
                                setPlanHoverCount={setPlanHoverCount}
                                updateError={updateError}
                                isPaymentUpdateError={!!updateError}
                                pendingInvoiceUrl={
                                    updateError?.data?.hostedInvoiceUrl
                                }
                                onCancelSubscription={handleCancelSubscription}
                                onPauseSubscription={handlePauseSubscription}
                                onContactSupport={handleContactSupport}
                                hasActiveSubscription={!!primarySubscription}
                                onNavigateToCalculator={() => setActiveTab("module-costs")}
                                referralCode={referralCode}
                                t={t}
                            />
                            {/* Social Proof Section */}
                            <Card className="p-6 md:p-8 text-center border-border/50 shadow-[var(--shadow-soft)] border-2 bg-gray-50 mt-8">
                                <p className="text-lg md:text-xl font-semibold text-foreground">
                                    {t("billing.joinCompanies")}
                                </p>
                            </Card>
                            <FAQ />
                            <Security />

                        </>
                    )}

                    {/* Credits Tab */}
                    {activeTab === "credits" && (
                        <CreditsTab
                            user={user}
                            companyAgentUICredit={companyAgentUICredit}
                            primarySubscription={primarySubscription}
                            paymentMethods={paymentMethods}
                            handleGetCompanyAgentUICredit={
                                handleGetCompanyAgentUICredit
                            }
                            getUser={getUser}
                            highlightPlatform={highlightPlatform}
                            monthlyCredits={currentPlanDetails?.credits ? parseInt(currentPlanDetails.credits) : 200}
                            onNavigateToCalculator={() => setActiveTab("module-costs")}
                            t={t}
                        />
                    )}

                    {activeTab === "usage" && (
                        <UsageTab t={t} />
                    )}

                    {/* Support Tab */}
                    {activeTab === "support" && <SupportTab paymentMethods={paymentMethods} />}

                    {/* Invoices Tab */}
                    {activeTab === "invoices" && <InvoicesTab user={user} t={t} />}

                    {/* Module Cost Calculator Tab */}
                    {activeTab === "module-costs" && <ModuleCostsTab />}
                </div>
            </div>

            {/* Sticky Mobile CTA Footer - Mobile Only - Only show on plans tab and if no active subscription */}
            {activeTab === "plans" && !currentSubscription && (
            <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-lg safe-area-inset-bottom">
                <div className="px-4 py-3">
                    <Button
                        onClick={(e) => {
                            e.preventDefault();
                            // Find recommended plan or first available plan
                            const availablePlans = plans.filter(
                                (plan) =>
                                    !plan.hidden &&
                                    plan.name !== "Build with You" &&
                                    plan.name !== "Enterprise Plus" &&
                                    plan.price !== "Contact us"
                            );

                            // Try to find recommended plan first
                            let planToSelect = availablePlans.find(
                                (plan) => plan.recommended
                            );

                            // If no recommended, use first available
                            if (!planToSelect && availablePlans.length > 0) {
                                planToSelect = availablePlans[0];
                            }

                            if (planToSelect) {
                                handlePlanSelection(planToSelect);
                                // Open checkout modal
                                setShowEmbeddedCheckout(true);
                            } else {
                                // Fallback: just scroll to plans
                                const plansSection = document.querySelector(
                                    "[data-plans-section]"
                                );
                                if (plansSection) {
                                    const offset = 80;
                                    const elementPosition =
                                        plansSection.getBoundingClientRect()
                                            .top;
                                    const offsetPosition =
                                        elementPosition +
                                        window.pageYOffset -
                                        offset;
                                    window.scrollTo({
                                        top: offsetPosition,
                                        behavior: "smooth",
                                    });
                                }
                            }
                        }}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white py-3 rounded-lg font-semibold text-base flex items-center justify-center gap-2"
                    >
                        <span role="img" aria-label="unlock">
                            🔓
                        </span>{" "}
                        {t("billing.choosePlan", { plan: "" })}
                    </Button>
                </div>
            </div>
            )}

            {/* Floating Sales Assistant */}
            {showFloatingSalesAssist && (
                <div
                    className="fixed bottom-6 right-6 z-50 max-w-sm"
                    style={{
                        animation:
                            "slideUpFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards",
                    }}
                >
                    <style>{`
                        @keyframes slideUpFadeIn {
                            from {
                                opacity: 0;
                                transform: translateY(20px) scale(0.95);
                            }
                            to {
                                opacity: 1;
                                transform: translateY(0) scale(1);
                            }
                        }
                    `}</style>
                    <div className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-6 border border-blue-200 backdrop-blur-sm relative overflow-hidden">
                        {/* Decorative elements */}
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
                        <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-indigo-400/20 to-blue-400/20 rounded-full blur-2xl -ml-12 -mb-12"></div>

                        <button
                            onClick={() => {
                                setShowFloatingSalesAssist(false);
                                localStorage.setItem(
                                    "floatingSalesAssistDismissed",
                                    "true"
                                );
                            }}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-2xl leading-none w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all z-50"
                            aria-label="Close"
                        >
                            ×
                        </button>

                        <div className="relative z-10 pr-6">
                            {/* Icon */}
                            <div className="mb-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <svg
                                        className="w-6 h-6 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                        />
                                    </svg>
                                </div>
                            </div>

                            <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                                {t("billing.conditionalSalesAssist.title")}
                            </h3>
                            <p className="text-sm text-gray-600 mb-5 leading-relaxed">
                                {t("billing.conditionalSalesAssist.subtitle")}
                            </p>
                            <a
                                href="https://calendar.app.google/r1NEgK7oWPMoUxur9"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center gap-2 w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-xl transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                                {t("billing.conditionalSalesAssist.bookCall")}
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Processing Modal for 3DS */}
            <PaymentProcessingModal
                isOpen={isPaymentProcessing}
                message={processingMessage}
            />
        </MainLayout>
    );
}

// Wrapper component that provides Stripe Elements context
export default function BillingPage() {
    return (
        <Elements stripe={stripePromise}>
            <SupportProvider>
                <BillingPageContent />
            </SupportProvider>
        </Elements>
    );
}
